import { api } from '@/lib/api';
import type { WhatsAppSyncStatus, SyncTrigger as ContractSyncTrigger } from '@valiosascrm/shared/contract';

// Story 17.3 — Frontend service for WhatsApp Initial Sync (Story 17.2 backend).
// Contract types come from the single source of truth (Prisma → @valiosascrm/shared/contract).

export type SyncStatus = WhatsAppSyncStatus;
export type SyncTrigger = ContractSyncTrigger;

export interface WhatsAppSyncJob {
  // 41.9 (RC-014): terminal parcial visível — "terminou" ≠ "deu certo".
  lastSyncOutcome?: 'completed' | 'completed_with_errors' | null;
  lastSyncWarning?: string | null;
  id: string;
  status: SyncStatus;
  triggeredBy: SyncTrigger;
  totalContacts: number;
  processed: number;
  // Story 26.12 — message-import phase progress (drives the 2-phase gate copy).
  messagesProcessed?: number;
  matchedPatients: number;
  matchedLeads: number;
  unmatched: number;
  skippedGroups: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  // Story 26.2 — server-side state machine field for the unified Inbox gate.
  phase?: 'DISCONNECTED' | 'SYNCING' | 'READY' | 'FAILED';
  lastConnectedAt?: string | null;
  isCancellable?: boolean;
  // Readiness flags (Story 26.x — first-load gating; legacy retrocompat)
  ready?: boolean;
  isFirstSync?: boolean;
  isConnected?: boolean;
  inProgress?: boolean;
  percent?: number;
  // Story 26.6 (AD-3 / AC2) — a complementary (delta) sync is running while the Inbox is
  // already READY. Drives a lightweight non-blocking indicator (not the full-page gate).
  isComplementarySyncing?: boolean;
}

export interface ResyncResponse {
  jobId: string;
  status: 'queued';
}

export interface CancelResponse {
  jobId: string;
  status: 'cancelled';
}

/** POST /whatsapp/resync — admin only. */
export async function resync(): Promise<ResyncResponse> {
  const { data } = await api.post<ResyncResponse>('/whatsapp/resync');
  return data;
}

/** GET /whatsapp/sync-status — returns the latest sync job (or null). */
export async function getSyncStatus(): Promise<WhatsAppSyncJob | null> {
  const { data } = await api.get<WhatsAppSyncJob | null>('/whatsapp/sync-status');
  return data;
}

/** POST /whatsapp/sync/:jobId/cancel — admin only. */
export async function cancelSync(jobId: string): Promise<CancelResponse> {
  const { data } = await api.post<CancelResponse>(`/whatsapp/sync/${jobId}/cancel`);
  return data;
}
